import { Formik } from 'formik';
import { useEffect, useMemo } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import Button from '../../components/button';
import Input from '../../components/input';
import Select from '../../components/Select';
import { findCollectionByKeys, normalizeOptions } from '../../utils/helper';
import { fetchIssueEquipmentDropdown, fetchIssueTyresDropdown, fetchIssueVehiclesDropdown } from '../../store/Slices/IssuesSlice';
import { getUserDropdown } from '../../store/Slices/UserSlice';
import { clearServiceEntryByVehicleDropdown, createWorkOrder, fetchWorkOrder, getServiceEntryByVehicleDropdown, getVendorsDropdown, getWorkOrderTagsDropdown } from '../../store/Slices/WorkOrderSlice';
import '../../styles/Issue.css';
import { CreateWorkOrderValidationSchema } from '../../utils/Schema';
import { errorNotify, successNotify } from '../../utils/toast';

const AssetSourceTypeEnum = {
    Vehicle: 'vehicle',
    Equipment: 'equipment',
    Tyre: 'tyre',
};

const assetTypeOptions = [
    { label: 'Vehicle', value: AssetSourceTypeEnum.Vehicle },
    { label: 'Equipment', value: AssetSourceTypeEnum.Equipment },
    { label: 'Tyre', value: AssetSourceTypeEnum.Tyre },
];

const repairClassOptions = [
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'unscheduled', label: 'Unscheduled' },
];

const getPayloadId = (value) => {
    if (value === '' || value === null || value === undefined) {
        return value;
    }

    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) ? parsedValue : value;
};

const CreateWorkOrder = ({ onClose, searchText, searchFilters, currentPage, limit }) => {
    const dispatch = useDispatch();

    const userDetails = JSON.parse(localStorage.getItem("fms-maintenance-details"));

    const { userDropdownLoading, userDropdownData } = useSelector((state) => state.users);
    const {
        issueVehiclesDropdownData,
        issueVehiclesDropdownLoading,
        issueEquipmentDropdownData,
        issueEquipmentDropdownLoading,
        issueTyresDropdownData,
        issueTyresDropdownLoading,
    } = useSelector((state) => state.issues);

    const {
        getVendorsDropdownData,
        getVendorsDropdownLoading,
        getServiceEntryByVehicleDropdownData,
        getServiceEntryByVehicleDropdownLoading,
        createWorkOrderLoading,
        getWorkOrderTagsDropdownData,
        getWorkOrderTagsDropdownLoading,
    } = useSelector((state) => state.workOrder);

    useEffect(() => {
        dispatch(clearServiceEntryByVehicleDropdown());
        dispatch(getUserDropdown());
        dispatch(fetchIssueVehiclesDropdown());
        dispatch(fetchIssueEquipmentDropdown());
        dispatch(fetchIssueTyresDropdown());
        dispatch(getVendorsDropdown());
        dispatch(getWorkOrderTagsDropdown());

        return () => {
            dispatch(clearServiceEntryByVehicleDropdown());
        };
    }, [dispatch]);

    const userOptions = (userDropdownData || []).map((user) => ({
        value: user.value,
        label: user.label,
        image: user.image,
        showIcon: true
    }));

    const vendorOptions = (getVendorsDropdownData || []).map((vendor) => ({
        value: vendor.value,
        label: vendor.title,
    }));

    const tagsOptions = (getWorkOrderTagsDropdownData || []).map((tag) => ({
        value: tag?.tag_name,
        label: tag?.tag_name,
    }));

    const vehicleOptions = useMemo(() => {
        const source = Array.isArray(issueVehiclesDropdownData)
            ? issueVehiclesDropdownData
            : findCollectionByKeys(issueVehiclesDropdownData, [
                'vehicles',
                'vehicle',
                'vehiclesdropdown',
                'vehicledropdown',
                'vehiclelist',
            ]);

        return normalizeOptions(source, {
            valueKeys: ['value', 'id', 'vehicle_id', 'ref_id'],
            labelKeys: ['label', 'registration_number', 'registration_no', 'vehicle_registration_number', 'name', 'title'],
            defaultLabelPrefix: 'Vehicle',
        });
    }, [issueVehiclesDropdownData]);

    const equipmentOptions = useMemo(() => {
        const source = Array.isArray(issueEquipmentDropdownData)
            ? issueEquipmentDropdownData
            : findCollectionByKeys(issueEquipmentDropdownData, [
                'equipments',
                'equipmentdropdown',
                'equipment',
                'equipmentlist',
            ]);

        return normalizeOptions(source, {
            valueKeys: ['value', 'id', 'equipment_id'],
            labelKeys: ['label', 'equipment_id', 'title', 'name', 'serial_number', 'registration_number'],
            defaultLabelPrefix: 'Equipment',
        });
    }, [issueEquipmentDropdownData]);

    const tyreOptions = useMemo(() => {
        const source = Array.isArray(issueTyresDropdownData)
            ? issueTyresDropdownData
            : findCollectionByKeys(issueTyresDropdownData, [
                'tyres',
                'tyre',
                'tyresdropdown',
                'tyredropdown',
                'tyrelist',
            ]);

        return normalizeOptions(source, {
            valueKeys: ['value', 'id', 'tyre_id', 'system_id'],
            labelKeys: ['label', 'tyre_id', 'serial_number', 'system_id', 'name', 'title'],
            defaultLabelPrefix: 'Tyre',
        });
    }, [issueTyresDropdownData]);

    const serviceEntryOptions = (getServiceEntryByVehicleDropdownData || []).map((service) => ({
        value: service.id,
        label: service.service_id,
    }));

    const handleAssetTypeChange = (selectedOption, setFieldValue) => {
        const nextAssetType = selectedOption?.value || '';

        setFieldValue('asset_type', nextAssetType);
        setFieldValue('asset_id', '');
        setFieldValue('service_ids', []);
        dispatch(clearServiceEntryByVehicleDropdown());
    };

    const handleAssetChange = async (selectedOption, assetType, setFieldValue) => {
        setFieldValue('asset_id', selectedOption?.value || '');
        setFieldValue('service_ids', []);
        dispatch(clearServiceEntryByVehicleDropdown());

        if (!selectedOption || !assetType) {
            return;
        }

        dispatch(getServiceEntryByVehicleDropdown({
            assetType,
            assetId: selectedOption?.value,
        })).unwrap();
    };

    const initialValues = {
        asset_type: '',
        asset_id: '',
        repair_class: '',
        issued_at: new Date().toLocaleDateString("en-CA"),
        scheduled_start_at: '',
        expected_completion_at: '',
        issued_by: userDetails?.id || '',
        assigned_to: '',
        vendor_id: '',
        invoice_no: '',
        service_ids: [],
        tags: [],
    };

    const handleSubmit = async (values, { setSubmitting, resetForm }) => {
        try {
            const data = {
                asset_id: getPayloadId(values.asset_id),
                asset_type: values.asset_type,
                repair_class: values.repair_class,
                issued_at: values.issued_at,
                scheduled_start_at: values.scheduled_start_at,
                expected_completion_at: values.expected_completion_at,
                issued_by: parseInt(values.issued_by),
                assigned_to: parseInt(values.assigned_to),
                vendor_id: parseInt(values.vendor_id),
                invoice_no: values.invoice_no,
                service_ids: values.service_ids.map((s) => parseInt(s.value || s)),
                tags: values.tags.map((tag) => tag.value || tag),
            };

            await dispatch(createWorkOrder(data)).unwrap();
            successNotify('Work order created successfully');
            onClose();
            resetForm();

            if (searchText.length > 0 || searchFilters) {
                dispatch(fetchWorkOrder({
                    page: currentPage,
                    limit: limit,
                    search_text: searchText,
                    searchFilters: searchFilters
                }));
            }
            else {
                dispatch(fetchWorkOrder({
                    page: currentPage,
                    limit: limit,
                }));
            }
        } catch (error) {
            errorNotify(error);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={CreateWorkOrderValidationSchema}
            onSubmit={handleSubmit}
            enableReinitialize={true}
        >
            {({ values, errors, touched, handleChange, handleBlur, setFieldValue, submitForm }) => {
                const selectedAssetType = values.asset_type || '';
                const currentAssetOptions = selectedAssetType === AssetSourceTypeEnum.Vehicle
                    ? vehicleOptions
                    : selectedAssetType === AssetSourceTypeEnum.Equipment
                        ? equipmentOptions
                        : selectedAssetType === AssetSourceTypeEnum.Tyre
                            ? tyreOptions
                            : [];
                const currentAssetLoading = selectedAssetType === AssetSourceTypeEnum.Vehicle
                    ? issueVehiclesDropdownLoading
                    : selectedAssetType === AssetSourceTypeEnum.Equipment
                        ? issueEquipmentDropdownLoading
                        : selectedAssetType === AssetSourceTypeEnum.Tyre
                            ? issueTyresDropdownLoading
                            : false;
                const currentAssetLabel = selectedAssetType === AssetSourceTypeEnum.Vehicle
                    ? 'Vehicle'
                    : selectedAssetType === AssetSourceTypeEnum.Equipment
                        ? 'Equipment'
                        : selectedAssetType === AssetSourceTypeEnum.Tyre
                            ? 'Tyre'
                            : 'Asset';
                const currentAssetPlaceholder = selectedAssetType
                    ? `Select ${currentAssetLabel}`
                    : 'Select Asset Type First';
                const canLinkServiceEntries = Boolean(selectedAssetType && values.asset_id);

                return (
                    <div>
                        <Row>
                            <Col md={12}>
                                <Select
                                    label="Assets Type"
                                    placeholder="Select Asset Type"
                                    options={assetTypeOptions}
                                    loading={false}
                                    name="asset_type"
                                    value={assetTypeOptions.find((option) => option.value === values.asset_type) || null}
                                    onChange={(selectedOption) => handleAssetTypeChange(selectedOption, setFieldValue)}
                                    onBlur={handleBlur}
                                    error={errors.asset_type}
                                    touched={touched.asset_type}
                                    isSearch={false}
                                />
                            </Col>
                            <Col md={12}>
                                <Select
                                    label={currentAssetLabel}
                                    placeholder={currentAssetPlaceholder}
                                    options={currentAssetOptions}
                                    loading={currentAssetLoading}
                                    name="asset_id"
                                    value={currentAssetOptions.find((option) => String(option.value) === String(values.asset_id)) || null}
                                    onChange={(selectedOption) => handleAssetChange(selectedOption, selectedAssetType, setFieldValue)}
                                    onBlur={handleBlur}
                                    error={errors.asset_id}
                                    touched={touched.asset_id}
                                    isSearch={false}
                                    isDisabled={!selectedAssetType}
                                />
                            </Col>
                            <Col md={12}>
                                <Select
                                    label="Repair Class"
                                    placeholder="Select Repair Class"
                                    options={repairClassOptions}
                                    name="repair_class"
                                    value={repairClassOptions.find((option) => option.value === values.repair_class) || null}
                                    onChange={(option) => setFieldValue('repair_class', option?.value || '')}
                                    onBlur={handleBlur}
                                    error={errors.repair_class}
                                    touched={touched.repair_class}
                                    isSearch={false}
                                />
                            </Col>
                            <Col md={12}>
                                <Input
                                    label="Issued At"
                                    placeholder=""
                                    type="date"
                                    name="issued_at"
                                    value={values.issued_at}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={errors.issued_at}
                                    touched={touched.issued_at}
                                    disabled={true}
                                />
                            </Col>
                            <Col md={6}>
                                <Input
                                    label="Scheduled Start Date"
                                    placeholder=""
                                    type="date"
                                    name="scheduled_start_at"
                                    value={values.scheduled_start_at}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={errors.scheduled_start_at}
                                    touched={touched.scheduled_start_at}
                                />
                            </Col>
                            <Col md={6}>
                                <Input
                                    label="Expected Completion Date"
                                    placeholder=""
                                    type="date"
                                    name="expected_completion_at"
                                    value={values.expected_completion_at}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={errors.expected_completion_at}
                                    touched={touched.expected_completion_at}
                                />
                            </Col>
                            <Col md={12}>
                                <Select
                                    label="Issued By"
                                    placeholder="Please Select"
                                    options={userOptions}
                                    loading={userDropdownLoading}
                                    name="issued_by"
                                    value={userOptions.find((option) => option.value === values.issued_by)}
                                    onChange={(option) => setFieldValue('issued_by', option?.value || '')}
                                    onBlur={handleBlur}
                                    error={errors.issued_by}
                                    touched={touched.issued_by}
                                    isSearch={false}
                                    isDisabled={true}
                                />
                            </Col>
                            <Col md={12}>
                                <Select
                                    label="Assignee"
                                    placeholder="Please Select"
                                    options={userOptions}
                                    loading={userDropdownLoading}
                                    name="assigned_to"
                                    value={userOptions.find((option) => option.value === values.assigned_to)}
                                    onChange={(option) => setFieldValue('assigned_to', option?.value || '')}
                                    onBlur={handleBlur}
                                    error={errors.assigned_to}
                                    touched={touched.assigned_to}
                                    isSearch={false}
                                />
                            </Col>
                            <Col md={12}>
                                <Select
                                    label="Vendor"
                                    placeholder="Select Vendor"
                                    options={vendorOptions}
                                    loading={getVendorsDropdownLoading}
                                    name="vendor_id"
                                    value={vendorOptions.find((option) => option.value === values.vendor_id)}
                                    onChange={(option) => setFieldValue('vendor_id', option?.value || '')}
                                    onBlur={handleBlur}
                                    error={errors.vendor_id}
                                    touched={touched.vendor_id}
                                    isSearch={false}
                                />
                            </Col>
                            <Col md={12}>
                                <Input
                                    label="Invoice Number"
                                    placeholder="Enter Invoice Number"
                                    type="text"
                                    name="invoice_no"
                                    value={values.invoice_no}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={errors.invoice_no}
                                    touched={touched.invoice_no}
                                />
                            </Col>
                            <Col md={12}>
                                <Select
                                    label="Link Service Entry"
                                    placeholder={selectedAssetType ? `Select Service Entries related to the ${currentAssetLabel.toLowerCase()}` : 'Select Asset Type First'}
                                    options={selectedAssetType ? serviceEntryOptions : []}
                                    loading={canLinkServiceEntries ? getServiceEntryByVehicleDropdownLoading : false}
                                    name="service_ids"
                                    value={(selectedAssetType ? serviceEntryOptions : []).filter((option) =>
                                        values.service_ids && values.service_ids.some((sel) => (sel.value || sel) === option.value)
                                    )}
                                    onChange={(selectedOptions) =>
                                        setFieldValue('service_ids', selectedOptions || [])
                                    }
                                    onBlur={handleBlur}
                                    error={errors.service_ids}
                                    touched={touched.service_ids}
                                    isSearch={false}
                                    isMulti={true}
                                    isDisabled={!canLinkServiceEntries}
                                />
                            </Col>
                            <Col md={12}>
                                <Select
                                    label="Tags/Labels"
                                    placeholder="Add Tags/Labels"
                                    options={tagsOptions}
                                    loading={getWorkOrderTagsDropdownLoading}
                                    name="tags"
                                    value={values.tags}
                                    onChange={(selectedOptions) => setFieldValue('tags', selectedOptions || [])}
                                    onBlur={handleBlur}
                                    isMulti={true}
                                    isCreatable={true}
                                    error={errors.tags && typeof errors.tags === 'string' ? errors.tags : null}
                                    touched={touched.tags}
                                />
                            </Col>
                            <Col md={12}>
                                <Button
                                    cancelBtn={onClose}
                                    submitBtn={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        submitForm();
                                    }}
                                    loading={createWorkOrderLoading}
                                    disabled={createWorkOrderLoading}
                                    submitText="Save"
                                    type="button"
                                />
                            </Col>
                        </Row>
                    </div>
                );
            }}
        </Formik>
    );
};

export default CreateWorkOrder;
